import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Button, DataGrid, LoadPanel, Popup } from "devextreme-react";
import NumberBox from "devextreme-react/number-box";
import { SingleMultipleOrNone } from "devextreme/common";
import {
  Column,
  ColumnChooser,
  Editing,
  Export,
  FilterRow,
  Form,
  HeaderFilter,
  Item,
  MasterDetail,
  Paging,
  SearchPanel,
  Sorting,
  Toolbar,
} from "devextreme-react/data-grid";

import {
  loadData,
  saveChange,
  setChanges,
  setEditRowKey,
} from "../../components/dataGridActionUtil";
import { dataGridRreducer, DataGridStateInitialState } from "../../components/dataGridReducerUtil";
import { getDataGridColumns, getDataGridEditorFormItems } from "../../components/dataGridUtil";

import { CrudDataGridProps } from "./model";

export const CrudDataGrid = (props: CrudDataGridProps) => {
  props.logger.debug("Rendering CrudDataGrid component");
  const dataGridRef = useRef<DataGrid<any, any>>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState<any>(null);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isEditing, setIsEditing] = useState(false);
  const [state, dispatch] = useReducer(dataGridRreducer, DataGridStateInitialState);

  const logEvent = useCallback((eventName: string) => {
    props.logger.debug(`Event: ${eventName}`);
  }, [props.logger]);

  const onChangesChange = useCallback((changes: any[]) => {
    logEvent("onChangesChange");
    setChanges(dispatch, changes);
  }, [logEvent]);

  const onEditRowKeyChange = useCallback((editRowKey: any) => {
    logEvent("onEditRowKeyChange");
    setEditRowKey(dispatch, editRowKey);
  }, [logEvent]);
  
  const onSaving = useCallback((e: any) => {
    logEvent("onSaving");
    e.cancel = true;
    e.promise = saveChange(dispatch, e.changes[0], props.sendChange);
  }, [logEvent, props.sendChange]);

  const refreshDataGrid = useCallback(
    (hasInactive: boolean) => {
      setSelectedRowKey(null);
      dataGridRef.current?.instance?.clearSelection();
      loadData(dispatch, async () => {
        return await props.refreshData(hasInactive);
      });
    },
    [props.refreshData],
  );

  useEffect(() => {
    refreshDataGrid(showInactive);
  }, [props.refreshData]);

  const toggleShowInactive = () => {
    const newValue = !showInactive;
    refreshDataGrid(newValue);
    setShowInactive(newValue);
  };

  const rowData = useMemo(
    () => (state.data && state.data.length > 0 ? state.data[0] : props.dummyRow),
    [state.data, props.dummyRow]
  );
  const rowDataForForm = useMemo(
    () => props.filterRowDataForForm(rowData),
    [rowData, props.filterRowDataForForm]
  );
  const selectionMode: SingleMultipleOrNone = props.selectionMode ?? ("single" as SingleMultipleOrNone);
  const selection = useMemo(() => ({ mode: selectionMode }), [selectionMode]);

  const handleSelectionChanged = useCallback(
    (e: any) => {
      logEvent("onSelectionChanged");
      setSelectedRowKey(e.selectedRowKeys?.[0] ?? null);
      if (props.onSelectionChanged) {
        props.onSelectionChanged(e.selectedRowsData);
      }
    },
    [props.onSelectionChanged, logEvent],
  );

  const getSelectedRowIndex = () => {
    const instance = dataGridRef.current?.instance;
    if (!instance) {
      return -1;
    }
    const key = selectedRowKey ?? instance.getSelectedRowKeys()[0];
    if (key === undefined || key === null) {
      return -1;
    }
    return instance.getRowIndexByKey(key);
  };

  const handleAddRow = () => {
    dataGridRef.current?.instance.addRow();
  };

  const handleEditRow = () => {
    const instance = dataGridRef.current?.instance;
    if (!instance) {
      return;
    }
    const rowIndex = getSelectedRowIndex();
    if (rowIndex >= 0) {
      instance.editRow(rowIndex);
    }
  };

  const handleDeleteRow = () => {
    const instance = dataGridRef.current?.instance;
    if (!instance) {
      return;
    }
    const rowIndex = getSelectedRowIndex();
    if (rowIndex >= 0) {
      instance.deleteRow(rowIndex);
    }
  };

  const handlePageSizeChange = useCallback(
    (value: number | null | undefined) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return;
      }
      const safeValue = parsed > 0 ? Math.floor(parsed) : 1;
      setPageSize(safeValue);
    },
    [],
  );

  const hasSelection = selectedRowKey !== undefined && selectedRowKey !== null;

  const onEditingStart = useCallback(() => {
    logEvent("EditingStart");
    setIsEditing(true);
  }, [logEvent]);
  const onInitNewRow = useCallback(() => {
    logEvent("InitNewRow");
    setIsEditing(false);
  }, [logEvent]);
  const onRowInserting = useCallback(() => logEvent("RowInserting"), [logEvent]);
  const onRowInserted = useCallback(() => logEvent("RowInserted"), [logEvent]);
  const onRowUpdating = useCallback(() => logEvent("RowUpdating"), [logEvent]);
  const onRowUpdated = useCallback(() => logEvent("RowUpdated"), [logEvent]);
  const onRowRemoving = useCallback(() => logEvent("RowRemoving"), [logEvent]);
  const onRowRemoved = useCallback(() => logEvent("RowRemoved"), [logEvent]);
  const onSaved = useCallback(() => logEvent("Saved"), [logEvent]);
  const onEditCanceling = useCallback(() => logEvent("EditCanceling"), [logEvent]);
  const onEditCanceled = useCallback(() => logEvent("EditCanceled"), [logEvent]);

  const keyColumns = useMemo(
    () => (Array.isArray(props.keyExpr) ? props.keyExpr : [props.keyExpr]),
    [props.keyExpr]
  );

  const formItems = useMemo(
    () =>
      getDataGridEditorFormItems(
        rowDataForForm,
        props.formColCount,
        props.formColSpan,
        props.editorItemOptions,
        keyColumns,
        isEditing,
        props.isKeyAutoGenerated,
        props.columnConfigs,
      ),
    [rowDataForForm, props.formColCount, props.formColSpan, props.editorItemOptions, keyColumns, isEditing, props.isKeyAutoGenerated, props.columnConfigs]
  );

  const columns = useMemo(
    () => getDataGridColumns(rowData, 
      keyColumns, 
      props.excludeKeyColumns, props.columnConfigs, props.excludedColumns),
    [rowData, keyColumns, props.excludeKeyColumns, props.columnConfigs, props.excludedColumns]
  );

  // Memoize popup configuration to prevent remounting
  const popupConfig = useMemo(
    () => ({ title: props.formTitle, showTitle: true, width: 700, height: 600 }),
    [props.formTitle]
  );

  return (
    <>
      <DataGrid
        ref={dataGridRef}
        dataSource={state.data}
        keyExpr={props.keyExpr}
        showBorders={true}
        allowColumnResizing={true}
        columnResizingMode="widget"
        columnMinWidth={30}
        columnAutoWidth={true}
        onEditingStart={onEditingStart}
        onInitNewRow={onInitNewRow}
        onRowInserting={onRowInserting}
        onRowInserted={onRowInserted}
        onRowUpdating={onRowUpdating}
        onRowUpdated={onRowUpdated}
        onRowRemoving={onRowRemoving}
        onRowRemoved={onRowRemoved}
        onSaving={onSaving}
        onSaved={onSaved}
        onEditCanceling={onEditCanceling}
        onEditCanceled={onEditCanceled}
        selection={selection}
        onSelectionChanged={handleSelectionChanged}
        onEditorPreparing={props.onEditorPreparing}
      >
        <SearchPanel
          visible={!props.hideSearchPanel}
          highlightCaseSensitive={true}
          placeholder={props.pageProps.appUtil.getLocaleString("search")}
        />
        <ColumnChooser enabled={!props.hideColumnChooser} />
        <Sorting mode="multiple" />
        <HeaderFilter visible={true} />
        <FilterRow visible={props.showFilterRow} />
        <Paging enabled={true} pageSize={pageSize} />
        <Export enabled={true} />
        <Toolbar>
          <Item location="before">
            <Button icon="refresh" hint="Refresh" onClick={() => refreshDataGrid(showInactive)} />
          </Item>
          <Item location="before" name="columnChooserButton" />
          <Item location="before" name="exportButton" />
          {props.allowAdding && (
            <Item location="before">
              <Button icon="add" hint="Add" onClick={handleAddRow} />
            </Item>
          )}
          {props.allowUpdating && (
            <Item location="before">
              <Button icon="edit" hint="Edit" disabled={!hasSelection} onClick={handleEditRow} />
            </Item>
          )}
          {props.allowDeleting && (
            <Item location="before">
              <Button
                icon="trash"
                hint="Delete"
                disabled={!hasSelection}
                onClick={handleDeleteRow}
              />
            </Item>
          )}
          {props.additionalButtons &&
            props.additionalButtons.map((ButtonComponent, index) => (
              <Item location="before" key={index}>{ButtonComponent}</Item>
            ))}
          {!props.hideInactiveButton && (
            <Item location="after">
              <Button
                text={showInactive ? "Active Only" : "Show Inactive"}
                width="168"
                onClick={toggleShowInactive}
              />
            </Item>
          )}
          <Item location="after" name="applyFilterButton" />
          <Item location="after" name="searchPanel" />
          <Item location="center">
            <div className="informer" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>Total Count: {state.data.length}</span>
              <span>Page Size:</span>
              <NumberBox
                min={1}
                showSpinButtons={true}
                value={pageSize}
                width={120}
                onValueChanged={(e) => handlePageSizeChange(e.value)}
              />
            </div>
          </Item>
        </Toolbar>
        {columns}
        <Column type="buttons" visible={false} />
        <Editing
          mode="popup"
          allowAdding={props.allowAdding}
          allowUpdating={props.allowUpdating}
          allowDeleting={props.allowDeleting}
          changes={state.changes}
          onChangesChange={onChangesChange}
          editRowKey={state.editRowKey}
          onEditRowKeyChange={onEditRowKeyChange}
        >
          <Popup {...popupConfig} />
          <Form>
            {formItems}
          </Form>
        </Editing>
        {props.masterDetailComponent && (
          <MasterDetail enabled={true} component={props.masterDetailComponent} />
        )}
      </DataGrid>
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={state.isLoading}
        showIndicator
        shading
        showPane
      />
    </>
  );
};
